import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import {getAppSetting} from "./AppSettings";

let awsRumInstance: AwsRum | null = null;
let pendingEvents: Array<{type: string; data: Record<string, unknown>}> = [];

/**
 * Returns the CloudWatch RUM instance (null if not initialized or in local stage).
 */
export function getAwsRum(): AwsRum | null {
    return awsRumInstance;
}

/**
 * Records a custom event to CloudWatch RUM for observability.
 * If RUM is not yet initialized, buffers the event for later flush.
 */
export function recordRumEvent(eventType: string, eventData: Record<string, unknown>) {
    if (awsRumInstance) {
        try {
            awsRumInstance.recordEvent(eventType, eventData);
        } catch (e) {
            // Don't let telemetry failures affect the app
        }
    } else {
        pendingEvents.push({type: eventType, data: eventData});
    }
}

/**
 * Flushes any events that were recorded before RUM was initialized.
 */
function flushPendingEvents() {
    if (awsRumInstance && pendingEvents.length > 0) {
        for (const event of pendingEvents) {
            try {
                awsRumInstance.recordEvent(event.type, event.data);
            } catch (e) {
                // Don't let telemetry failures affect the app
            }
        }
        pendingEvents = [];
    }
}

/**
 * Drains pending events to console when RUM will never be initialized
 * (e.g. auth failure path). Ensures telemetry isn't silently lost.
 */
export function drainPendingEventsToFallback() {
    for (const event of pendingEvents) {
        console.warn('[RUM fallback]', event.type, event.data);
    }
    pendingEvents = [];
}

/**
 * Returns basic network info from the browser (if available).
 * Useful for correlating slow loads with network conditions.
 */
export function getNetworkInfo(): Record<string, unknown> {
    const nav = navigator as any;
    const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
    if (!conn) {
        return { networkInfoAvailable: false };
    }
    return {
        networkInfoAvailable: true,
        effectiveType: conn.effectiveType ?? 'unknown',  // e.g. '4g', '3g', '2g'
        downlinkMbps: conn.downlink ?? null,              // estimated bandwidth
        rtt: conn.rtt ?? null,                            // round-trip time in ms
        saveData: conn.saveData ?? false
    };
}

export default function initializeCloudWatchRUM() {
    if (getAppSetting("stage") === "local"){
        return;
    }
    const rumConfig = getAppSetting("rumConfig")
    try {
        const config: AwsRumConfig = {
            sessionSampleRate: 1,
            guestRoleArn: rumConfig["guestRoleArn"],
            identityPoolId: rumConfig["identityPoolId"],
            endpoint: rumConfig["endpoint"],
            telemetries: ["errors", "performance", ["http",{recordAllRequests: true}]],
            allowCookies: true,
            enableXRay: false
        };
        const APPLICATION_VERSION: string = '1.0.0';

        awsRumInstance = new AwsRum(
            rumConfig['rumApplicationId'],
            APPLICATION_VERSION,
            rumConfig['rumApplicationRegion'],
            config
        );
        flushPendingEvents();
    } catch (error) {
        // Ignore errors thrown during CloudWatch RUM web client initialization
    }
}